import React, { useState, useEffect } from 'react';
import { Card, Form, Input, InputNumber, Button, Space, message, Spin, Select } from 'antd';
import { SaveOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { axiosInstance, API_BASE_URL } from '../../providers/dataProvider';

interface SettingItem {
  key: string;
  value: string | null;
  type: string;
  description?: string | null;
}

const FRIEND_CHALLENGE_KEYS = new Set([
  'gamification.friend_challenge_policy_template',
  'gamification.friend_challenge_max_open',
  'gamification.friend_challenge_max_invitees',
  'gamification.friend_challenge_block_overlapping_invites',
  'gamification.friend_challenge_coins_per_strike_day',
]);

const POLICY_TEMPLATE_PRESETS = {
  A: {
    'gamification.friend_challenge_policy_template': 'A',
    'gamification.friend_challenge_max_open': 1,
    'gamification.friend_challenge_max_invitees': 5,
    'gamification.friend_challenge_block_overlapping_invites': 1,
  },
  B: {
    'gamification.friend_challenge_policy_template': 'B',
    'gamification.friend_challenge_max_open': 3,
    'gamification.friend_challenge_max_invitees': 10,
    'gamification.friend_challenge_block_overlapping_invites': 1,
  },
} as const;

const NUMBER_KEYS = new Set([
  'gamification.enabled',
  'gamification.daily_task_count',
  'gamification.base_xp',
  'gamification.base_coins',
  'gamification.daily_cap_xp',
  'gamification.daily_cap_coins',
  'gamification.freeze_price_coins',
  'gamification.friend_challenge_max_open',
  'gamification.friend_challenge_max_invitees',
  'gamification.friend_challenge_block_overlapping_invites',
  'gamification.friend_challenge_coins_per_strike_day',
  'gamification.safety_low_calories',
  'gamification.safety_high_steps',
  'gamification.safety_high_exercise_minutes',
  'gamification.rapid_completion_threshold',
  'gamification.rapid_completion_window_minutes',
  'gamification.daily_refresh_limit',
]);

const JSON_KEYS = new Set([
  'gamification.category_weights',
  'gamification.difficulty_multipliers',
  'gamification.streak_milestones',
  'gamification.streak_goal',
  'gamification.pet_skins',
]);

const SELECT_KEYS = new Set(['gamification.friend_challenge_policy_template']);

function formatLabel(key: string): string {
  return key
    .replace('gamification.', '')
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export const GamificationSettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<SettingItem[]>([]);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<'A' | 'B'>('B');

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get<SettingItem[]>(
        `${API_BASE_URL}/admin/gamification/settings`,
      );
      const items = Array.isArray(data) ? data : [];
      setSettings(items);
      const formValues: Record<string, string | number | null> = {};
      for (const s of items) {
        if (NUMBER_KEYS.has(s.key)) {
          formValues[s.key] = s.value ? Number(s.value) : null;
        } else {
          formValues[s.key] = s.value ?? '';
        }
      }
      form.setFieldsValue(formValues);
      const templateValue = String(
        formValues['gamification.friend_challenge_policy_template'] ?? 'B',
      ).toUpperCase();
      setSelectedTemplate(templateValue === 'A' ? 'A' : 'B');
    } catch {
      message.error('Failed to load gamification settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const applyTemplate = (template: 'A' | 'B') => {
    const preset = POLICY_TEMPLATE_PRESETS[template];
    form.setFieldsValue(preset);
    setSelectedTemplate(template);
    message.info(`Applied template ${template} defaults to the form (save to persist)`);
  };

  const handleSave = async () => {
    const values = form.getFieldsValue();
    const changes: Array<{ key: string; value: string }> = [];

    for (const s of settings) {
      const newVal = values[s.key];
      const strVal = NUMBER_KEYS.has(s.key)
        ? String(newVal ?? '')
        : String(newVal ?? '');
      const currentVal = s.value ?? '';
      if (strVal !== currentVal) {
        changes.push({ key: s.key, value: strVal });
      }
    }

    if (changes.length === 0) {
      message.info('No changes to save');
      return;
    }

    for (const c of changes) {
      if (JSON_KEYS.has(c.key)) {
        try {
          JSON.parse(c.value);
        } catch {
          message.error(`Invalid JSON for ${formatLabel(c.key)}`);
          return;
        }
      }
    }

    setSaving(true);
    try {
      await axiosInstance.put(`${API_BASE_URL}/admin/gamification/settings/batch`, {
        settings: changes,
      });
      message.success(`${changes.length} setting(s) updated`);
      fetchSettings();
    } catch {
      message.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const renderField = (s: SettingItem) => {
    if (SELECT_KEYS.has(s.key)) {
      return (
        <Form.Item
          key={s.key}
          name={s.key}
          label={formatLabel(s.key)}
          extra={s.description}
        >
          <Select
            options={[
              { value: 'A', label: 'A — Strict (1 open, 5 invitees, no overlap)' },
              { value: 'B', label: 'B — Moderate (3 open, 10 invitees, no overlap)' },
            ]}
            onChange={(value: 'A' | 'B') => setSelectedTemplate(value)}
          />
        </Form.Item>
      );
    }
    if (NUMBER_KEYS.has(s.key)) {
      return (
        <Form.Item
          key={s.key}
          name={s.key}
          label={formatLabel(s.key)}
          extra={s.description}
        >
          <InputNumber style={{ width: '100%' }} />
        </Form.Item>
      );
    }
    if (JSON_KEYS.has(s.key)) {
      return (
        <Form.Item
          key={s.key}
          name={s.key}
          label={formatLabel(s.key)}
          extra={s.description ?? 'Valid JSON object or array'}
        >
          <Input.TextArea rows={4} />
        </Form.Item>
      );
    }
    return (
      <Form.Item key={s.key} name={s.key} label={formatLabel(s.key)} extra={s.description}>
        <Input />
      </Form.Item>
    );
  };

  const friendChallengeSettings = settings.filter((s) => FRIEND_CHALLENGE_KEYS.has(s.key));
  const otherSettings = settings.filter((s) => !FRIEND_CHALLENGE_KEYS.has(s.key));

  return (
    <Spin spinning={loading}>
      <div style={{ padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 style={{ margin: 0 }}>Gamification Settings</h2>
          <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
            Save Changes
          </Button>
        </div>
        <Form form={form} layout="vertical">
          <Card
            title="Friend Challenge Policy"
            style={{ marginBottom: 16 }}
            extra={
              <Space>
                <Select
                  value={selectedTemplate}
                  style={{ width: 280 }}
                  options={[
                    { value: 'A', label: 'Template A — Strict' },
                    { value: 'B', label: 'Template B — Moderate' },
                  ]}
                  onChange={(value: 'A' | 'B') => setSelectedTemplate(value)}
                />
                <Button
                  icon={<ThunderboltOutlined />}
                  onClick={() => applyTemplate(selectedTemplate)}
                >
                  Apply Template Defaults
                </Button>
              </Space>
            }
          >
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              {friendChallengeSettings.map((s) => renderField(s))}
            </Space>
          </Card>
          <Card title="Other Gamification Settings">
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              {otherSettings.map((s) => renderField(s))}
            </Space>
          </Card>
        </Form>
      </div>
    </Spin>
  );
};
